import { zodResolver } from "@hookform/resolvers/zod"
import { DefaultValues, useForm } from "react-hook-form"
import {
  BigBlueBackorderConfigSchema,
  BigBlueCampaignTriggerEnum,
  BIGBLUE_BACKORDER_DEFAULT_CONFIG,
  CampaignDetailResponseDataSchema,
  CampaignTriggerIntegrationSchema,
  CampaignTriggerTypeEnum,
  ChannelEnum,
  EmailCampaignChannelIntegrationSchema,
  EmailCampaignTriggerMetadataSchema,
  Errors,
  UpdateCampaignBaseSchema,
  WhatsappMessageStatusUpdateEnum,
  validateEventHasAction,
} from "schema"
import { z } from "zod"

const EmailTriggerMetadataFormSchema = EmailCampaignTriggerMetadataSchema.extend({
  gmailAccount: z.object({ value: z.coerce.string() }).optional(),
  gmailAccounts: z.object({ values: z.array(z.coerce.string()) }).optional(),
  outlookAccount: z.object({ value: z.coerce.string() }).optional(),
  outlookAccounts: z.object({ values: z.array(z.coerce.string()) }).optional(),
})

const BigBlueBackorderConfigFormSchema = z.object({
  incomingThreshold: z.preprocess(
    (value) => (value === "" ? Number.NaN : value),
    z.number({ error: "" }).finite({ error: "" }).int({ error: "" }).nonnegative({ error: "" }),
  ),
  skuMatchStrategy: BigBlueBackorderConfigSchema.shape.skuMatchStrategy,
})

const BigBlueCampaignTriggerIntegrationFormSchema = z.object({
  type: z.literal("bigblue"),
  trigger: z.enum(BigBlueCampaignTriggerEnum),
  backorder: BigBlueBackorderConfigFormSchema.optional(),
  email: EmailCampaignChannelIntegrationSchema.nullish(),
})

const CampaignTriggerIntegrationFormSchema = z.union([
  CampaignTriggerIntegrationSchema,
  BigBlueCampaignTriggerIntegrationFormSchema,
])

const OUTBOUND_INTEGRATION_TRIGGERS = [
  CampaignTriggerTypeEnum.Shopify,
  CampaignTriggerTypeEnum.BigBlue,
] as const

function valuesFromMetadata(
  account: { value: string } | undefined,
  accounts: { values: string[] } | undefined,
) {
  if (accounts) return accounts.values
  if (account) return [account.value]
  return []
}

export const UpdateCampaignSchemaFrontend = UpdateCampaignBaseSchema.omit({
  templates: true,
})
  .extend({
    triggerIntegration: CampaignTriggerIntegrationFormSchema.nullish(),
    templates: z.array(
      z.object({
        id: z.string(),
        language: z.string(),
        name: z.string(),
        status: WhatsappMessageStatusUpdateEnum.optional(),
        header: z.string().nullish(),
        body: z.string().optional(),
        footer: z.string().nullish(),
        mediaUrl: z.string().nullish(),
        mediaFormat: z.string().nullish(),
        whatsappTemplateButtons: z.array(z.object({ text: z.string() })).nullish(),
        whatsappTemplateVariables: z
          .array(
            z.object({
              paramName: z.string(),
              example: z.string(),
              description: z.string().nullish(),
            }),
          )
          .nullish(),
      }),
    ),
    openingMessages: z.array(
      z.object({
        id: z.string(),
        locale: z.string(),
        identifier: z.string(),
      }),
    ),
  })
  // Mirror the backend's UpdateCampaignSchema refine so `formState.isValid`
  // reflects the same constraints the server enforces (e.g. every event must
  // have a downstream action).
  .refine(validateEventHasAction, {
    path: ["nodes"],
    message: Errors.CAMPAIGN_EVENT_WITHOUT_ACTION,
  })

export function buildCampaignFormDefaults(
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>,
): DefaultValues<z.infer<typeof UpdateCampaignSchemaFrontend>> {
  // Gmail and Instagram DM campaigns default to a 3-day disable-after-human-interaction window.
  let triggerIntegration = campaign.triggerIntegration ?? null
  const triggerMetadata = EmailTriggerMetadataFormSchema.safeParse(campaign.triggerMetadata)

  if (
    triggerMetadata.success &&
    campaign.channel === ChannelEnum.Gmail &&
    !OUTBOUND_INTEGRATION_TRIGGERS.includes(
      campaign.trigger as (typeof OUTBOUND_INTEGRATION_TRIGGERS)[number],
    )
  ) {
    let gmailIntegrationIds = valuesFromMetadata(
      triggerMetadata.data.gmailAccount,
      triggerMetadata.data.gmailAccounts,
    )
    if (triggerIntegration?.type === "gmail") {
      gmailIntegrationIds = [
        ...(triggerIntegration.gmailIntegrationIds ?? []),
        ...(triggerIntegration.gmailIntegrationId ? [triggerIntegration.gmailIntegrationId] : []),
      ]
    }

    triggerIntegration = {
      type: "gmail",
      gmailIntegrationId: gmailIntegrationIds[0],
      gmailIntegrationIds,
      delayInDays:
        triggerIntegration?.type === "gmail"
          ? triggerIntegration.delayInDays
          : triggerMetadata.data.delayInDays,
      openingEmail:
        triggerIntegration?.type === "gmail"
          ? (triggerIntegration.openingEmail ?? triggerMetadata.data.openingEmail)
          : triggerMetadata.data.openingEmail,
      openingEmailLocaleOrder:
        triggerIntegration?.type === "gmail"
          ? (triggerIntegration.openingEmailLocaleOrder ??
            triggerMetadata.data.openingEmailLocaleOrder)
          : triggerMetadata.data.openingEmailLocaleOrder,
    }
  }

  if (
    triggerIntegration?.type === "bigblue" &&
    triggerIntegration.trigger === BigBlueCampaignTriggerEnum.Backorder
  ) {
    triggerIntegration = {
      ...triggerIntegration,
      backorder: triggerIntegration.backorder ?? BIGBLUE_BACKORDER_DEFAULT_CONFIG,
    }
  }

  if (
    triggerMetadata.success &&
    campaign.channel === ChannelEnum.Outlook &&
    !OUTBOUND_INTEGRATION_TRIGGERS.includes(
      campaign.trigger as (typeof OUTBOUND_INTEGRATION_TRIGGERS)[number],
    )
  ) {
    let outlookIntegrationIds = valuesFromMetadata(
      triggerMetadata.data.outlookAccount,
      triggerMetadata.data.outlookAccounts,
    )
    if (triggerIntegration?.type === "outlook") {
      outlookIntegrationIds = [
        ...(triggerIntegration.outlookIntegrationIds ?? []),
        ...(triggerIntegration.outlookIntegrationId
          ? [triggerIntegration.outlookIntegrationId]
          : []),
      ]
    }

    triggerIntegration = {
      type: "outlook",
      outlookIntegrationId: outlookIntegrationIds[0],
      outlookIntegrationIds,
      delayInDays:
        triggerIntegration?.type === "outlook"
          ? triggerIntegration.delayInDays
          : triggerMetadata.data.delayInDays,
      openingEmail:
        triggerIntegration?.type === "outlook"
          ? (triggerIntegration.openingEmail ?? triggerMetadata.data.openingEmail)
          : triggerMetadata.data.openingEmail,
      openingEmailLocaleOrder:
        triggerIntegration?.type === "outlook"
          ? (triggerIntegration.openingEmailLocaleOrder ??
            triggerMetadata.data.openingEmailLocaleOrder)
          : triggerMetadata.data.openingEmailLocaleOrder,
    }
  }

  if (
    triggerIntegration?.type === "instagramDirectMessage" ||
    triggerIntegration?.type === "gmail" ||
    triggerIntegration?.type === "outlook"
  ) {
    triggerIntegration = {
      ...triggerIntegration,
      delayInDays: triggerIntegration.delayInDays ?? 3,
    }
  }

  const trigger =
    campaign.trigger === CampaignTriggerTypeEnum.Klaviyo ? null : (campaign.trigger ?? null)

  return {
    // RHF's form.reset() treats `undefined` field values as "keep the initial
    // defaultValue" — so a refetch that reports a cleared field can silently
    // restore the original value. Use `null` for nullable fields so reset
    // actually overwrites them.
    flow: campaign.flow ?? null,
    name: campaign.name ?? "",
    description: campaign.description ?? "",
    toneOfVoice: campaign.toneOfVoice ?? "",
    trigger,
    triggerIntegration,
    defaultLanguage: campaign.defaultLanguage ?? null,
    channel: campaign.channel ?? null,
    whatsappNumberId: campaign.whatsappNumberId ?? null,
    callbackUrl: campaign.callbackUrl ?? "",
    callbackPayload: campaign.callbackPayload ?? null,
    callbackHeaders: campaign.callbackHeaders?.map((h) => ({ key: h.key, value: h.value })) ?? [],
    templates:
      campaign.templates?.map((template) => ({
        id: template.id,
        language: template.language,
        name: template.name,
        ...(template.channel === "Whatsapp" && {
          status: template.status,
          header: template.header,
          body: template.body,
          footer: template.footer,
          mediaUrl: template.mediaUrl,
          mediaFormat: template.mediaFormat,
          whatsappTemplateButtons: template.whatsappTemplateButtons,
          whatsappTemplateVariables: template.whatsappTemplateVariables,
        }),
      })) ?? [],
    openingMessages: campaign.openingMessages ?? [],
    goals: campaign.goals?.map((campaignGoal) => campaignGoal.goal) ?? [],
    nodes: campaign.nodes.map(({ action, conditions, event, ...rest }) => {
      let type: "Event" | "Condition" | "Action" = "Event"
      if (conditions && conditions.length > 0) type = "Condition"
      if (action) type = "Action"

      if (type === "Event") {
        return {
          ...rest,
          type: "Event",
          event: event?.type,
          config: event?.config,
        }
      }

      if (type === "Condition") {
        return {
          ...rest,
          type: "Condition",
          conditions,
        }
      }

      return {
        ...rest,
        type: "Action",
        action: action?.type,
        config: action?.config,
      }
    }),
  }
}

export function useCampaignForm(campaign: z.infer<typeof CampaignDetailResponseDataSchema>) {
  return useForm<z.infer<typeof UpdateCampaignSchemaFrontend>>({
    resolver: zodResolver(UpdateCampaignSchemaFrontend),
    defaultValues: buildCampaignFormDefaults(campaign),
    // Keep formState.isValid live so consumers (e.g. Builder Save button)
    // can disable actions while the form is invalid.
    mode: "onChange",
  })
}
